import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { tagChipStyle } from '@/util/tagChip'

interface Area {
  key: string
  label: string
  replace?: boolean
}

interface GroupRow {
  id: number
  name: string
  archived?: number
  copy_status?: string | null
  games?: string | Array<{ id: number; name: string }> | null
}

interface Matchable {
  map: string
  table: string
  label: string
  source: Array<{ id: number; name: string }>
  targets: Array<{ id: number; name: string }>
  proposed: Record<string, number | null>
}

interface Preview {
  counts?: Record<string, number | string>
  replaces?: Array<{ table: string; area: string }>
  skipped?: Record<string, number>
  matchables?: Matchable[]
  familyCounts?: Record<string, Array<{ label: string; n: number }>>
  collections?: Record<string, number>
  warnings?: string[]
  blocking?: string[]
}

type Mode = 'new' | 'copy_existing' | 'move'

// Same keys and order as the copy-company-group lambda's AREA_KEYS. `replace` marks the areas
// that overwrite one-row-per-group settings when copying into an existing location.
interface Family { key: string; label: string; stays: string }
// Same keys as FAMILY_KEYS in copy-company-group and the worker. `stays` is the preview line
// when the family is left unticked.

export default /*#__PURE__*/_defineComponent({
  __name: 'CopyGroupDrawer',
  props: {
    show: { type: Boolean },
    group: null,
    groups: null,
    loading: { type: Boolean },
    preview: null,
    previewLoading: { type: Boolean },
    canMove: { type: Boolean }
  },
  emits: ["close", "copy", "preview", "run", "preview-reset"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'close'): void
  (e: 'copy', payload: { name: string; code: string; areas: Record<string, boolean> }): void
  (e: 'preview', payload: Record<string, unknown>): void
  (e: 'run', payload: Record<string, unknown>): void
  (e: 'preview-reset'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  show: boolean
  group?: GroupRow | null
  groups?: GroupRow[]
  loading?: boolean
  preview?: Preview | null
  previewLoading?: boolean
  canMove?: boolean
}

const AREAS: Area[] = [
  { key: 'events', label: 'Events' },
  { key: 'checklists', label: 'Checklists' },
  { key: 'schedules', label: 'Schedules (copied inactive)' },
  { key: 'custom_fields', label: 'Custom fields and tags' },
  { key: 'booking_site', label: 'Booking site templates' },
  // request_forms is supported by the copy lambdas; add the row back when request forms ship (OTC-482).
  { key: 'payments', label: 'Payment settings, fees, taxes and payment pages', replace: true },
  { key: 'products', label: 'Items, gift card types and pass types' },
  { key: 'promo_codes', label: 'Promo codes and promo code groups' },
  { key: 'waivers', label: 'Waiver template', replace: true },
  { key: 'surveys', label: 'Surveys' },
  { key: 'messaging', label: 'Email and SMS', replace: true },
  { key: 'booking_settings', label: 'Booking statuses and calendar settings', replace: true },
  { key: 'venue_settings', label: 'Tracker, photo, staff and time log settings', replace: true },
  { key: 'dashboards_reports', label: 'Dashboards and reports' },
]

const TABLE_LABELS: Record<string, [string, string]> = {
  games: ['event', 'events'], checklists: ['checklist', 'checklists'], game_schedules: ['schedule', 'schedules'],
  custom_fields: ['custom field', 'custom fields'], tags: ['tag', 'tags'],
  booking_site_settings: ['booking site template', 'booking site templates'], request_forms: ['request form', 'request forms'],
  fees: ['fee', 'fees'], taxes: ['tax', 'taxes'], custom_payment_methods: ['payment method', 'payment methods'],
  payment_pages: ['payment page', 'payment pages'], items: ['item', 'items'], gift_card_types: ['gift card type', 'gift card types'],
  pass_types: ['pass type', 'pass types'], promo_code_templates: ['promo code group', 'promo code groups'],
  promo_codes: ['promo code', 'promo codes'], surveys: ['survey', 'surveys'], email_templates: ['email template', 'email templates'],
  sms_templates: ['SMS template', 'SMS templates'], email_automation_rules: ['automation (added inactive)', 'automations (added inactive)'],
  workflow_states: ['workflow state', 'workflow states'], booking_slot_status_types: ['booking status', 'booking statuses'],
  time_log_categories: ['time log category', 'time log categories'], dashboards: ['dashboard', 'dashboards'], reports: ['report', 'reports'],
}
function tableLabel(table: string, n: number): string {
  const pair = TABLE_LABELS[table]
  if (!pair) return table.replace(/_/g, ' ')
  return Number(n) === 1 ? pair[0] : pair[1]
}

const REPLACE_LABELS: Record<string, string> = {
  payment_settings: 'payment settings', gift_card_settings: 'gift card page settings', waiver_configurations: 'waiver template',
  automated_email_settings: 'automated email sender name and reply-to, and each automated email with the same type',
  calendar_policy_settings: 'calendar settings', game_tracker_settings: 'tracker settings', photo_settings: 'photo settings',
  staff_management_settings: 'staff settings',
}

const MOVE_ACKS = [
  'The source location is archived when the merge finishes. Anything left unticked stays on it; unarchive it to see it again.',
  'Bookings, orders, photos and history leave the source location\'s reports and appear on the target\'s.',
  'Payments taken before the merge were charged through the source location\'s payment gateway. Refunds after the merge go through the target\'s gateway and succeed only while both locations use the same account.',
  'The merged events are removed from the source booking site and are not added to the target booking site. Pending reminders send from the target location\'s email and SMS settings.',
]
const REPLACE_ACK = 'This replaces the target location\'s current settings for the ticked areas. The previous values are not kept.'

const FAMILIES: Family[] = [
  { key: 'photos', label: 'Photos', stays: 'Photos stay on the archived location; they still point at event records that moved.' },
  { key: 'passes_credits', label: 'Passes and customer credits', stays: 'Passes and credits stay on the archived location and cannot be used on the target.' },
  { key: 'customers', label: 'Customers (link them to the target, with tags, consents and notes)', stays: 'Customers are not linked to the target; their orders and waivers still move.' },
  { key: 'projects', label: 'Projects and work tickets', stays: 'Projects and work tickets stay on the archived location.' },
  { key: 'time_logs', label: 'Time logs', stays: 'Time logs stay on the archived location.' },
  { key: 'messaging_history', label: 'Email and SMS history, campaigns and audiences', stays: 'Sent emails, texts, campaigns and audiences stay on the archived location.' },
  { key: 'resources', label: 'Resources and their assignments', stays: 'Resources stay behind; future bookings lose their assignments and autoblocking re-runs on the target.' },
  { key: 'schedules', label: 'Schedules', stays: 'Schedules stay on the archived location; the moved events are unlinked from them.' },
  { key: 'carts', label: 'Carts and abandoned carts', stays: 'Carts stay on the archived location.' },
]





const mode = ref<Mode>('new')
const name = ref('')
const code = ref('')
const selectedAreas = ref<string[]>([])
const targetGroupId = ref<number | null>(null)
const selectedFamilies = ref<string[]>(FAMILIES.map((f) => f.key))
const matches = ref<Record<string, Record<string, number | null>>>({})
const acks = ref<boolean[]>([])
const confirmName = ref('')
const errors = ref<{ name: string; code: string }>({ name: '', code: '' })
const touched = ref<{ name: boolean; code: boolean }>({ name: false, code: false })

const modeOptions = computed(() => {
  const opts = [
    { value: 'new', label: 'Copy to new location' },
    { value: 'copy_existing', label: 'Copy to existing location' },
  ]
  if (props.canMove) opts.push({ value: 'move', label: 'Merge' })
  return opts
})

const MODE_HINTS: Record<Mode, string> = {
  new: 'Creates a new location from this one\'s settings and events. Nothing live comes with it: no bookings, orders, customers or connections.',
  copy_existing: 'Adds this location\'s settings and events to a location you already have. Lists are added next to what is there; replace areas overwrite that location\'s settings.',
  move: 'Merges this whole location into another one: events, bookings, orders, history and settings move with their ids, and this location is archived when the merge finishes.',
}
const modeHint = computed(() => MODE_HINTS[mode.value])

// Same rules as the group settings page (GroupSettings.vue validFields) and the
// copy-company-group lambda.
function validateName(): boolean {
  const v = name.value.trim()
  if (v.length === 0) errors.value.name = 'Name is required'
  else if (v.length > 50) errors.value.name = 'Name cannot be more than 50 characters'
  else errors.value.name = ''
  return errors.value.name === ''
}

function validateCode(): boolean {
  const v = code.value.trim()
  if (v.length === 0) errors.value.code = 'Code is required'
  else if (/\s/.test(code.value)) errors.value.code = 'Code cannot contain spaces'
  else if (v.length > 25) errors.value.code = 'Code cannot be more than 25 characters'
  else errors.value.code = ''
  return errors.value.code === ''
}

const sourceName = computed(() => (props.group && props.group.name) || '')
const drawerTitle = computed(() => {
  if (mode.value === 'move') return sourceName.value ? `Merge ${sourceName.value} into another location` : 'Merge location'
  return sourceName.value ? `Copy ${sourceName.value}` : 'Copy or merge'
})

const targetOptions = computed(() => {
  const sourceId = props.group && props.group.id
  return (props.groups || []).filter((g) =>
    g.id !== sourceId && Number(g.archived || 0) === 0 && (!g.copy_status || g.copy_status === 'ready'))
})
const targetName = computed(() => {
  const t = targetOptions.value.find((g) => g.id === targetGroupId.value)
  return t ? t.name : ''
})

const allSelected = computed(() => selectedAreas.value.length === AREAS.length)
const isIndeterminate = computed(() => selectedAreas.value.length > 0 && !allSelected.value)
const replaceTicked = computed(() => AREAS.some((a) => a.replace && selectedAreas.value.includes(a.key)))

const canPreview = computed(() => {
  if (!targetGroupId.value) return false
  if (mode.value === 'copy_existing') return selectedAreas.value.length > 0
  if (mode.value === 'move') return true
  return false
})

const hasBlocking = computed(() => !!(props.preview && props.preview.blocking && props.preview.blocking.length > 0))

const needsConfirmation = computed(() => {
  if (!props.preview || hasBlocking.value) return false
  if (mode.value === 'move') return true
  return mode.value === 'copy_existing' && replaceTicked.value
})

const acknowledgments = computed(() => (mode.value === 'move' ? MOVE_ACKS : [REPLACE_ACK]))

// What an unmatched row becomes: a copy when copying, its own moved record when merging.
const unmatchedLabel = computed(() => (mode.value === 'move' ? 'Move as its own record' : 'Create copy'))

const matchablesToShow = computed<Matchable[]>(() =>
  ((props.preview && props.preview.matchables) || []).filter((m) => m.source.length > 0 && m.targets.length > 0))

// Matchable tables where the target has nothing to reuse collapse to one preview line.
const createOnlyLines = computed<string[]>(() =>
  ((props.preview && props.preview.matchables) || [])
    .filter((m) => m.source.length > 0 && m.targets.length === 0)
    .map((m) => {
      const n = m.source.length
      const label = tableLabel(m.table, n) === m.table.replace(/_/g, ' ') ? m.label.toLowerCase() : tableLabel(m.table, n)
      return mode.value === 'move'
        ? `Moves ${n} ${label} as ${n === 1 ? 'it is' : 'they are'} (none exist on the target to reuse)`
        : `Creates ${n} ${label} on the target (none exist there to reuse)`
    }))

function formatCount(n: number | string, singular: string, plural?: string): string {
  const v = Number(n)
  return `${v.toLocaleString()} ${v === 1 ? singular : (plural || singular)}`
}

const previewLines = computed<string[]>(() => {
  const p = props.preview
  if (!p) return []
  const lines: string[] = []
  if (mode.value === 'copy_existing') {
    for (const [table, n] of Object.entries(p.counts || {})) {
      if (Number(n) > 0) lines.push(`Adds ${Number(n).toLocaleString()} ${tableLabel(table, Number(n))}`)
    }
    for (const r of p.replaces || []) lines.push(`Replaces the target's ${REPLACE_LABELS[r.table] || r.table.replace(/_/g, ' ')}`)
    for (const [table, n] of Object.entries(p.skipped || {})) {
      if (Number(n) > 0) lines.push(`Skips ${Number(n).toLocaleString()} ${tableLabel(table, Number(n))} that already ${Number(n) === 1 ? 'exists' : 'exist'} on the target`)
    }
    if (lines.length === 0) lines.push('Nothing to copy for the ticked areas')
  } else if (p.counts) {
    const c = p.counts
    const archivedNote = Number(c.archived_events || 0) > 0 ? ` (plus ${formatCount(c.archived_events, 'archived event', 'archived events')} with their history, still archived on the target)` : ''
    lines.push(`Moves ${formatCount(c.events || 0, 'event', 'events')}${archivedNote} with ${formatCount(c.bookings || 0, 'booking', 'bookings')}, ${formatCount(c.orders || 0, 'order', 'orders')}, ${formatCount(c.instances || 0, 'event record', 'event records')}, ${formatCount(c.waivers || 0, 'waiver', 'waivers')}, ${formatCount(c.booking_requests || 0, 'booking request', 'booking requests')} and ${formatCount(c.gift_cards || 0, 'gift card sold', 'gift cards sold')}`)
    const settings = Object.values(p.collections || {}).reduce((a, n) => a + Number(n || 0), 0)
    if (settings > 0) lines.push(`Moves ${settings.toLocaleString()} settings records (templates, surveys, dashboards, reports, checklists, promo groups and more); booking site templates and automations arrive switched off`)
    for (const f of FAMILIES) {
      const parts = (p.familyCounts && p.familyCounts[f.key]) || []
      const total = parts.reduce((a, x) => a + Number(x.n || 0), 0)
      if (selectedFamilies.value.includes(f.key)) {
        if (total > 0) lines.push(`Moves ${parts.filter((x) => Number(x.n) > 0).map((x) => formatCount(x.n, x.label, x.label)).join(', ')}`)
        if (f.key === 'customers' && Number(c.customers_to_link || 0) > 0) lines.push(`Links ${formatCount(c.customers_to_link, 'customer', 'customers')} to the target`)
      } else if (total > 0) {
        lines.push(`Stays: ${f.stays}`)
      }
    }
    if (Number(c.active_site_links || 0) > 0) lines.push('Removes the events from the source booking site')
    lines.push('Archives the source location')
  }
  for (const l of createOnlyLines.value) lines.push(l)
  return lines
})

const canSubmit = computed(() => {
  if (props.loading) return false
  if (mode.value === 'new') {
    return !errors.value.name && !errors.value.code && name.value.trim().length > 0 && code.value.trim().length > 0
  }
  if (!props.preview || hasBlocking.value || !targetGroupId.value) return false
  if (needsConfirmation.value) {
    if (!acknowledgments.value.every((_, i) => acks.value[i] === true)) return false
    if (confirmName.value.trim() !== targetName.value.trim()) return false
  }
  return true
})

const submitLabel = computed(() => {
  if (props.loading) return mode.value === 'move' ? 'Merging...' : 'Copying...'
  if (mode.value === 'copy_existing') return 'Copy settings'
  if (mode.value === 'move') return 'Merge location'
  return 'Copy'
})

function matchValue(map: string, sourceId: number): number {
  const m = matches.value[map]
  const v = m ? m[String(sourceId)] : null
  return v === null || v === undefined ? 0 : v
}

function setMatch(map: string, sourceId: number, value: number) {
  const next = { ...(matches.value[map] || {}) }
  next[String(sourceId)] = value === 0 ? null : value
  matches.value = { ...matches.value, [map]: next }
}

function seedMatchesFromPreview() {
  const out: Record<string, Record<string, number | null>> = {}
  for (const m of (props.preview && props.preview.matchables) || []) {
    out[m.map] = { ...(m.proposed || {}) }
  }
  matches.value = out
}

function resetPreview() {
  acks.value = acknowledgments.value.map(() => false)
  confirmName.value = ''
  matches.value = {}
  emit('preview-reset')
}

function onModeChange(v: string) {
  mode.value = v as Mode
  if (mode.value === 'copy_existing') selectedAreas.value = AREAS.filter((a) => !a.replace).map((a) => a.key)
  else if (mode.value === 'new') selectedAreas.value = AREAS.map((a) => a.key)
  resetPreview()
}

function onTargetChange(v: number) {
  targetGroupId.value = v
  resetPreview()
}

function onToggleAll(e: Event) {
  const checked = (e.target as HTMLInputElement).checked
  selectedAreas.value = checked ? AREAS.map((a) => a.key) : []
  resetPreview()
}

function areasPayload(): Record<string, boolean> {
  const areas: Record<string, boolean> = {}
  for (const a of AREAS) areas[a.key] = selectedAreas.value.includes(a.key)
  return areas
}

// Only matches whose source row is in the preview are sent, as { map: { sourceId: targetId } }
// with null for "create copy".
function matchesPayload(): Record<string, Record<string, number | null>> {
  const out: Record<string, Record<string, number | null>> = {}
  for (const [map, pairs] of Object.entries(matches.value)) {
    const kept: Record<string, number | null> = {}
    for (const [k, v] of Object.entries(pairs)) if (v !== null && v !== undefined) kept[k] = v
    if (Object.keys(kept).length > 0) out[map] = kept
  }
  return out
}

function basePayload(): Record<string, unknown> {
  const p: Record<string, unknown> = { mode: mode.value, target_group_id: targetGroupId.value }
  if (mode.value === 'copy_existing') p.areas = areasPayload()
  if (mode.value === 'move') {
    const families: Record<string, boolean> = {}
    for (const f of FAMILIES) families[f.key] = selectedFamilies.value.includes(f.key)
    p.families = families
  }
  return p
}

function onPreview() {
  if (!canPreview.value) return
  acks.value = acknowledgments.value.map(() => false)
  confirmName.value = ''
  emit('preview', basePayload())
}

function onClose() {
  emit('close')
}

function onSubmit() {
  if (mode.value === 'new') {
    touched.value = { name: true, code: true }
    const nameOk = validateName()
    const codeOk = validateCode()
    if (!nameOk || !codeOk) return
    emit('copy', { name: name.value.trim(), code: code.value.trim(), areas: areasPayload() })
    return
  }
  if (!canSubmit.value) return
  const payload = basePayload()
  payload.matches = matchesPayload()
  if (needsConfirmation.value) {
    payload.acknowledged = true
    payload.confirm_name = confirmName.value.trim()
  }
  emit('run', payload)
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.show) onClose()
}

// Validate as the user types (after the first keystroke in each field) and on submit.
watch(name, () => { if (touched.value.name) validateName() })
watch(code, () => { if (touched.value.code) validateCode() })

// A fresh preview seeds the match table and clears the confirmation state.
watch(() => props.preview, (p) => {
  if (p) {
    seedMatchesFromPreview()
    acks.value = acknowledgments.value.map(() => false)
    confirmName.value = ''
  }
})

watch(
  () => props.show,
  (visible) => {
    if (visible) {
      mode.value = 'new'
      name.value = `${sourceName.value} copy`.trim().slice(0, 50)
      code.value = ''
      errors.value = { name: '', code: '' }
      touched.value = { name: false, code: false }
      selectedAreas.value = AREAS.map((a) => a.key)
      targetGroupId.value = null
      selectedFamilies.value = FAMILIES.map((f) => f.key)
      matches.value = {}
      acks.value = []
      confirmName.value = ''
      emit('preview-reset')
      document.addEventListener('keydown', onEscKey)
    } else {
      document.removeEventListener('keydown', onEscKey)
    }
  }
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})

return { __sfc: true,AREAS, TABLE_LABELS, tableLabel, REPLACE_LABELS, MOVE_ACKS, REPLACE_ACK, FAMILIES, props, emit, mode, name, code, selectedAreas, targetGroupId, selectedFamilies, matches, acks, confirmName, errors, touched, modeOptions, MODE_HINTS, modeHint, validateName, validateCode, sourceName, drawerTitle, targetOptions, targetName, allSelected, isIndeterminate, replaceTicked, canPreview, hasBlocking, needsConfirmation, acknowledgments, unmatchedLabel, matchablesToShow, createOnlyLines, formatCount, previewLines, canSubmit, submitLabel, matchValue, setMatch, seedMatchesFromPreview, resetPreview, onModeChange, onTargetChange, onToggleAll, areasPayload, matchesPayload, basePayload, onPreview, onClose, onSubmit, onEscKey, tagChipStyle }
}

})